import { IconBell } from "central-icons/IconBell";
import { useCallback, type FC } from "react";

import { Notification as CountBadge } from "@/components/flexkit/badge/Notification";
import { SidebarButton } from "@/components/SidebarButton";
import { useNotificationList } from "@/hooks/use-notifications";
import { useSegmentTrack } from "@/hooks/use-segment";

type NotificationButtonProps = {
    isLocked: boolean;
    onDismissSidebarMenu?: () => void;
    notificationPanelOpen?: boolean;
    setNotificationPanelOpen?: (open: boolean) => void;
};

export const NotificationButton: FC<NotificationButtonProps> = ({
    isLocked,
    onDismissSidebarMenu,
    notificationPanelOpen,
    setNotificationPanelOpen,
}) => {
    const { unreadCount, totalCount } = useNotificationList();
    const segmentTrack = useSegmentTrack();

    const handleToggle = useCallback(() => {
        const willOpen = !notificationPanelOpen;
        setNotificationPanelOpen?.(willOpen);
        if (willOpen) {
            onDismissSidebarMenu?.();
            segmentTrack("activity_panel_opened", { unread_count: unreadCount, total_count: totalCount });
        }
    }, [notificationPanelOpen, setNotificationPanelOpen, onDismissSidebarMenu, segmentTrack, unreadCount, totalCount]);

    return (
        <SidebarButton
            className="w-full"
            icon={<IconBell size={20} />}
            label="Activity"
            iconRight={
                unreadCount > 0 ? (
                    <CountBadge
                        content={unreadCount > 99 ? "99+" : String(unreadCount)}
                        data-testid="activity-badge"
                    />
                ) : undefined
            }
            hideLabel={!isLocked}
            onClick={handleToggle}
            data-tracking-id="open-activity"
            data-testid="activity-button"
        />
    );
};
